import React, { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { useModeContext } from "../contexts/ModeContext";

// Modern/Professional segmented control with glass effect and animated indicator
const SegmentedControl = ({
  options = ["BC", "PP"],
  mobileInMenu = false,
  compact = false,
}) => {
  const { activeMode, setActiveMode, isTransitioning } = useModeContext();
  const root = useRef(null);
  const indicator = useRef(null);
  const itemsRef = useRef([]);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize indicator position on first render
  useEffect(() => {
    if (!root.current || !indicator.current) return;
    
    // Wait for next frame to ensure DOM is ready
    requestAnimationFrame(() => {
      const activeButton = itemsRef.current[activeMode];
      if (activeButton) {
        const x = activeButton.offsetLeft;
        const w = activeButton.offsetWidth;
        
        if (!isInitialized) {
          // Set initial position without animation
          gsap.set(indicator.current, { x, width: w });
          setIsInitialized(true);
        } else {
          // Animate to new position
          gsap.to(indicator.current, {
            x,
            width: w,
            duration: 0.4,
            ease: "power2.out"
          });
        }
      }
    });
  }, [activeMode, isInitialized]);

  // Handle resize to recalculate indicator position
  useEffect(() => {
    const handleResize = () => {
      if (!root.current || !indicator.current) return;
      
      const activeButton = itemsRef.current[activeMode];
      if (activeButton) {
        const x = activeButton.offsetLeft;
        const w = activeButton.offsetWidth;
        gsap.set(indicator.current, { x, width: w });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [activeMode]);

  const handleClick = (i) => {
    if (i === activeMode || isTransitioning) return;
    
    // Simple click feedback
    const button = itemsRef.current[i];
    if (button) {
      gsap.to(button, {
        scale: 0.95,
        duration: 0.1,
        ease: "power2.out",
        yoyo: true,
        repeat: 1
      });
    }
    
    // Change mode
    setActiveMode(i);
  };

  return (
    <div
      ref={root}
      className={`relative ${mobileInMenu ? "flex" : compact ? "flex" : "hidden sm:flex"} items-center rounded-full border ${activeMode === 1 ? 'border-white/20 bg-black/25' : 'border-white/60 bg-white/40'} backdrop-blur-md px-1 ${compact ? 'py-0.5 text-[10px]' : 'py-1 text-[11px]'} ${activeMode === 1 ? 'text-white/90' : 'text-neutral-700'} select-none shadow-sm hover:shadow transition-shadow`}
      style={{ width: "fit-content", height: compact ? 28 : 36 }}
      role="tablist"
      aria-label="Mode Switch"
    >
      {/* Indicator */}
      <span
        ref={indicator}
        className="absolute left-0 top-0 h-full rounded-full"
        style={{
          width: 0,
          background: "linear-gradient(135deg, var(--brand-accent) 0%, var(--brand-accent) 100%)",
          boxShadow: "inset 0 0 0 1px rgba(255, 255, 255, 0.3), 0 4px 15px rgba(212, 165, 141, 0.25), 0 2px 6px rgba(0,0,0,0.1)",
          backdropFilter: "blur(6px)",
          border: "1px solid rgba(255, 255, 255, 0.2)"
        }}
        aria-hidden
      />
      {/* subtle gloss */}
      <span
        className="pointer-events-none absolute inset-0 rounded-full"
        style={{
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0) 40%)",
          maskImage: "radial-gradient(80% 80% at 50% 0%, black, transparent)"
        }}
        aria-hidden
      />

      {options.map((label, i) => {
        // Show orange glow on Peak Performance button when Behaviour Change is active
        const shouldGlow = activeMode === 0 && i === 1;
        
        return (
          <button
            key={label}
            ref={(el) => (itemsRef.current[i] = el)}
            onClick={() => handleClick(i)}
            disabled={isTransitioning}
            role="tab"
            aria-selected={i === activeMode}
            aria-label={`${label === "BC" ? "Behaviour Change" : label === "PP" ? "Peak Performance" : label} Modus${i === activeMode ? " (aktiv)" : ""}`}
            className={`relative z-10 ${compact ? 'px-2 h-6' : 'px-3 h-7'} mx-0.5 rounded-full transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 ${
              isTransitioning 
                ? "opacity-50 cursor-not-allowed" 
                : i === activeMode 
                  ? "text-white font-medium cursor-default" 
                  : activeMode === 1 
                    ? "text-white/60 hover:text-white/90 cursor-pointer" 
                    : "text-neutral-600 hover:text-neutral-900 cursor-pointer"
            } ${shouldGlow ? 'orange-glow' : ''}`}
            style={shouldGlow ? {
              animation: 'orangeGlow 1.5s ease-in-out infinite',
              boxShadow: '0 0 4px rgba(212, 165, 141, 0.2), 0 0 8px rgba(212, 165, 141, 0.1)'
            } : {}}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
};

export default SegmentedControl;